import { FieldValues, UseFormSetError } from 'react-hook-form';

import { AccessGroup } from 'interfaces/AccessGroup';
import { Group } from 'interfaces/Group';
import { Permissions } from 'interfaces/Permissions';
import { RepositoryParams, handleErrorForm } from '@services/api';

import { create, list, listPermissions, retrieve, update } from './calls';
export * from './calls';

export const contexts = {
  users: 'Usuários',
  accessGroups: 'Níveis de Acesso',
  logs: 'Logs',
  groups: 'Grupos',
  emphasis: 'Destaques',
  report: 'Relatórios',
  dashboard: 'Dashboard',
  budgetTemplates: 'Templates de budget',
  costCenters: 'Centro de custo',
  costCentersFromTo: 'Centro de custo de/para',
  categories: 'Categorias',
  budgets: 'Budgets',
  transactions: 'Transações',
  files: 'Arquivos',
  budgetsConsolidated: 'Budgets Consolidados',
  collaborators: 'Colaboradores',
  collaborator_expenses: 'Estimativa de Folha',
  budget_expense_lines: 'Budget Despesas',
} as { [key: string]: string };

const resources = {
  read: 'Ler',
  create: 'Criar',
  update: 'Editar',
  delete: 'Excluir',
  import: 'Importar',
  accessGroups: 'Def. grupo',
  updatePassword: 'Mudar senha',
  setChosen: 'Definir',
  updateOrders: 'Ordenar',
  groups: 'Grupos',
  getDashboardReport: 'Dashboard',
} as { [key: string]: string };

const reverseContexts = Object.fromEntries(
  Object.entries(contexts).map(([key, value]) => [value, key])
);

const reverseResources = Object.fromEntries(
  Object.entries(resources).map(([key, value]) => [value, key])
);

export default class AccessGroupAPICaller {
  static adaptFromAPI = (data: AccessGroup) => {
    return {
      ...data,
      permissions: this.adaptPermissionsFromAPI(data.permissions),
    } as FieldValues;
  };
  static adaptToAPI = (data: FieldValues) => {
    return {
      ...data,
      permissions: this.adaptPermissionsToAPI(data.permissions),
    } as AccessGroup;
  };

  static adaptPermissionsListFromAPI = (data: {
    [context: string]: string[];
  }) => {
    const permissions = Object.fromEntries(
      Object.entries(data).map(([context, contextResources]) => [
        contexts[context] ?? context,
        contextResources.map((resource) => resources[resource]),
      ])
    );
    return permissions;
  };

  static adaptPermissionsFromAPI = (data: Permissions) => {
    const permissions = Object.fromEntries(
      Object.entries(data).map(([context, contextResources]) => [
        contexts[context] ?? context,
        Object.fromEntries(
          Object.entries(contextResources).map(([resource, value]) => [
            resources[resource] ?? resource,
            value,
          ])
        ),
      ])
    );

    return permissions;
  };

  static adaptPermissionsToAPI = (data: Permissions) => {
    const permissions = Object.fromEntries(
      Object.entries(data).map(([context, contextResources]) => [
        reverseContexts[context] ?? context,
        Object.fromEntries(
          Object.entries(contextResources).map(([resource, value]) => [
            reverseResources[resource] ?? resource,
            value,
          ])
        ),
      ])
    );
    return permissions;
  };

  static fetchGroups = async (filters: Partial<Group & RepositoryParams>) => {
    const { data } = await list(filters);
    return data;
  };

  static fetchGroup = async (editId: string) => {
    const {
      data: { group },
    } = await retrieve(editId);
    return this.adaptFromAPI(group);
  };

  static fetchPermissions = async () => {
    const { data } = await listPermissions();
    const permissions = this.adaptPermissionsListFromAPI(data.permissions);
    return permissions;
  };

  static createOrUpdate = async (
    data: FieldValues,
    setError: UseFormSetError<FieldValues>
  ) => {
    const method = data.id ? update : create;

    const result = method(this.adaptToAPI(data)).catch(
      handleErrorForm(setError)
    );

    return result;
  };
}
